import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bookmark } from "lucide-react";
import { useAuth } from "@/components/AuthProvider";
import { toast } from "sonner";

/**
 * Wave C point Q (Vania 7 Oct 2026) · "save" and "interested" are now ONE
 * action. The surface is called "interested" and uses the bookmark icon.
 * It still toggles `PUT /events/{id}/reactions/save` under the hood (the
 * stored field keeps backwards-compat with existing data). Every count,
 * list and copy that used "saved" now reads this single state.
 *
 * Behaviour:
 *   · Guests → route to /app/signin, preserving intent in sessionStorage.
 *   · Members → optimistic toggle against PUT /events/{id}/reactions/save.
 */
const API = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: `${API}/api` });

function bearer() {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// WW-206.a · every card mounted in the same moment shares one bulk call (≤60 ids each).
let queue = new Map();
let timer = null;
function flush() {
  const batch = queue;
  queue = new Map();
  timer = null;
  const ids = [...batch.keys()];
  for (let i = 0; i < ids.length; i += 60) {
    const chunk = ids.slice(i, i + 60);
    client.get("/events/reactions/summary", { params: { ids: chunk.join(",") }, headers: bearer() })
      .then((r) => chunk.forEach((id) => batch.get(id).forEach((cb) => cb(r.data[id] || null))))
      .catch(() => chunk.forEach((id) => batch.get(id).forEach((cb) => cb(null))));
  }
}
function loadSummary(id, cb) {
  if (!queue.has(id)) queue.set(id, []);
  queue.get(id).push(cb);
  if (!timer) timer = setTimeout(flush, 30);
}

export default function SaveLikeRow({ eventId, size = "sm", testIdPrefix = "" }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [saved, setSaved] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!eventId) return;
    loadSummary(eventId, (s) => {
      if (!alive) return;
      if (s) setSaved(!!s.own_saved);
      setReady(true);
    });
    return () => { alive = false; };
  }, [eventId, user?.user_id]);

  const gate = useCallback(() => {
    if (user) return true;
    try { sessionStorage.setItem("watuwako:next", window.location.pathname + window.location.search); } catch {}
    nav("/app/signin");
    return false;
  }, [user, nav]);

  const toggle = useCallback(async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!gate()) return;
    const cur = saved;
    const next = !cur;
    setSaved(next);
    try {
      await client.put(`/events/${eventId}/reactions/save`,
                       { on: next }, { headers: bearer() });
      // Wave 16.2 (Vania 2 Oct) · save_tap fires only when turning ON.
      // Unsaving is not a tap. Fire-and-forget; never block the UI.
      if (next) {
        import("@/lib/analytics").then((m) => m.trackSave(eventId));
      }
    } catch (err) {
      setSaved(cur);
      toast.error("could not save. try again in a bit.");
    }
  }, [gate, eventId, saved]);

  const px = size === "lg" ? 22 : 18;
  const box = size === "lg" ? 44 : 36;
  const YELLOW = "#FFD60A";

  return (
    <div className="flex items-center gap-1" data-testid={`${testIdPrefix}reactions-row`}
         onClick={(e) => e.stopPropagation()}>
      <button type="button"
              aria-label={saved ? "not interested" : "interested"}
              aria-pressed={saved}
              data-testid={`${testIdPrefix}interested-btn`}
              data-interested={saved ? "1" : "0"}
              onClick={toggle}
              title={saved ? "you're interested" : "mark as interested"}
              style={{ width: box, height: box, borderRadius: 999, background: "rgba(32,32,58,0.85)",
                       border: `1px solid ${saved ? YELLOW : "#2F2F52"}`, color: saved ? YELLOW : "#FFFFFF",
                       display: "grid", placeItems: "center", cursor: "pointer", opacity: ready ? 1 : 0.6 }}>
        <Bookmark size={px} fill={saved ? YELLOW : "none"} strokeWidth={2} />
      </button>
    </div>
  );
}
